import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Select,
  Space,
  Tag,
  Progress,
  Row,
  Col,
  message,
  Tooltip,
  Popconfirm,
  Divider,
  Typography,
  Statistic
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  DollarOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import moment from 'moment';
import api from '../api';

const { Option } = Select;
const { TextArea } = Input;
const { Title, Text } = Typography;

const formatNum = (val) => {
  if (val === null || val === undefined || isNaN(val) || val === '') return '0.00';
  return Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const EmployeeLoans = () => {
  const [loans, setLoans] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [statementModalVisible, setStatementModalVisible] = useState(false);
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [loanStatement, setLoanStatement] = useState(null);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [paymentForm] = Form.useForm();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 100 });

  useEffect(() => {
    fetchLoans();
    fetchEmployees();
  }, []);

  const fetchLoans = async () => {
    setLoading(true);
    try {
      const response = await api.get('/employee-loans');
      if (response.success) {
        setLoans(response.data);
      }
    } catch (error) {
      message.error('Failed to fetch loans');
    }
    setLoading(false);
  };

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/employees');
      if (response.success) {
        setEmployees(response.data);
      }
    } catch (error) {
      message.error('Failed to fetch employees');
    }
  };

  const openModal = () => {
    form.resetFields();
    setModalVisible(true);
  };

  const openEditModal = (loan) => {
    setSelectedLoan(loan);
    editForm.setFieldsValue({
      monthly_deduction: loan.monthly_deduction,
      status: loan.status,
      purpose: loan.purpose,
      remarks: loan.remarks
    });
    setEditModalVisible(true);
  };

  const openStatementModal = async (loan) => {
    setSelectedLoan(loan);
    try {
      const response = await api.get(`/employee-loans/${loan.id}/statement`);
      if (response.success) {
        setLoanStatement(response.data);
        setStatementModalVisible(true);
      }
    } catch (error) {
      message.error('Failed to fetch loan statement');
    }
  };

  const openPaymentModal = (loan) => {
    setSelectedLoan(loan);
    paymentForm.resetFields();
    setPaymentModalVisible(true);
  };

  const handleCreateLoan = async (values) => {
    try {
      const response = await api.post('/employee-loans', {
        ...values,
        loan_date: values.loan_date.format('YYYY-MM-DD'),
        expected_completion_date: values.expected_completion_date?.format('YYYY-MM-DD')
      });
      
      if (response.success) {
        message.success('Loan created successfully');
        setModalVisible(false);
        fetchLoans();
      } else {
        message.error(response.message || 'Failed to create loan');
      }
    } catch (error) {
      message.error('Failed to create loan');
    }
  };

  const handleUpdateLoan = async (values) => {
    try {
      const response = await api.put(`/employee-loans/${selectedLoan.id}`, values);
      
      if (response.success) {
        message.success('Loan updated successfully');
        setEditModalVisible(false);
        fetchLoans();
      } else {
        message.error(response.message || 'Failed to update loan');
      }
    } catch (error) {
      message.error('Failed to update loan');
    }
  };

  const handleDeleteLoan = async (id) => {
    try {
      const response = await api.delete(`/employee-loans/${id}`);
      if (response.success) {
        message.success('Loan deleted successfully');
        fetchLoans();
      } else {
        message.error(response.message || 'Failed to delete loan');
      }
    } catch (error) {
      message.error('Failed to delete loan');
    }
  };

  const handleManualPayment = async (values) => {
    try {
      const response = await api.post(`/employee-loans/${selectedLoan.id}/manual-payment`, values);
      
      if (response.success) {
        message.success('Payment recorded successfully');
        setPaymentModalVisible(false);
        fetchLoans();
      } else {
        message.error(response.message || 'Failed to record payment');
      }
    } catch (error) {
      message.error('Failed to record payment');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'blue';
      case 'completed': return 'green';
      case 'cancelled': return 'red';
      default: return 'default';
    }
  };

  const columns = [
    {
      title: 'S.No',
      key: 'serial',
      width: 60,
      render: (_, __, index) =>
        (pagination.current - 1) * pagination.pageSize + index + 1,
    },
    {
      title: 'Employee',
      dataIndex: ['employee', 'name'],
      key: 'employee_name',
      render: (name, record) => (
        <div>
          <div>{name}</div>
          <small style={{ color: '#666' }}>{record.employee?.emp_code}</small>
        </div>
      )
    },
    {
      title: 'Loan Amount',
      dataIndex: 'loan_amount',
      key: 'loan_amount',
      render: (amount) => formatNum(amount)
    },
    {
      title: 'Remaining Balance',
      dataIndex: 'remaining_balance',
      key: 'remaining_balance',
      render: (balance) => formatNum(balance)
    },
    {
      title: 'Monthly Deduction',
      dataIndex: 'monthly_deduction',
      key: 'monthly_deduction',
      render: (amount) => formatNum(amount)
    },
    {
      title: 'Progress',
      key: 'progress',
      render: (_, record) => (
        <div>
          <Progress 
            percent={record.progress_percentage} 
            size="small" 
            status={record.status === 'completed' ? 'success' : 'active'}
          />
          <small>{record.progress_percentage}% paid</small>
        </div>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {status.toUpperCase()}
        </Tag>
      )
    },
    {
      title: 'Loan Date',
      dataIndex: 'loan_date',
      key: 'loan_date',
      render: (date) => moment(date).format('DD/MM/YYYY')
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space>
          <Tooltip title="View Statement">
            <Button 
              icon={<FileTextOutlined />} 
              size="small" 
              onClick={() => openStatementModal(record)}
            />
          </Tooltip>
          <Tooltip title="Manual Payment">
            <Button 
              icon={<DollarOutlined />} 
              size="small" 
              onClick={() => openPaymentModal(record)}
              disabled={record.status !== 'active'}
            />
          </Tooltip>
          <Tooltip title="Edit Loan">
            <Button 
              icon={<EditOutlined />} 
              size="small" 
              onClick={() => openEditModal(record)}
            />
          </Tooltip>
          <Popconfirm 
            title="Are you sure to delete this loan?" 
            onConfirm={() => handleDeleteLoan(record.id)} 
            okText="Yes" 
            cancelText="No"
          >
            <Button icon={<DeleteOutlined />} size="small" danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const [search, setSearch] = useState('');

  const filteredLoans = loans.filter(loan => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    const empName = loan.employee ? loan.employee.name : '';
    const empCode = loan.employee ? loan.employee.emp_code : '';
    return empName.toLowerCase().includes(s) || empCode.toLowerCase().includes(s);
  });

  return (
    <div style={{ padding: '0px' }}>
      <div style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle" justify="space-between">
          <Col>
            <h2>Employee Loans</h2>
          </Col>
          <Col style={{ display: 'flex', gap: 12 }}>
            <Input.Search
              placeholder="Search by employee name or code..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: 300 }}
              allowClear
            />
            <Button 
              type="primary" 
              icon={<PlusOutlined />} 
              onClick={openModal}
            >
              Create Loan
            </Button>
          </Col>
        </Row>
      </div>

      <Card>
        <Table 
          sticky
          columns={columns} 
          dataSource={filteredLoans} 
          loading={loading}
          rowKey="id"
          pagination={{
            defaultPageSize: 100,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: ['10', '20', '30', '40', '50', '60', '70', '80', '90', '100'],
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} loans`,
            onChange: (page, pageSize) => setPagination({ current: page, pageSize }),
            onShowSizeChange: (current, size) => setPagination({ current: 1, pageSize: size }),
          }}
        />
      </Card>

      {/* Create Loan Modal */}
      <Modal
        title="Create Employee Loan"
        open={modalVisible}
        onOk={() => form.submit()}
        onCancel={() => setModalVisible(false)}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleCreateLoan}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item 
                name="employee_id" 
                label="Employee" 
                rules={[{ required: true, message: 'Please select employee' }]}
              >
                <Select placeholder="Select Employee">
                  {employees
                    .filter(emp => emp.status === 'active')
                    .map(emp => (
                      <Option key={emp.id} value={emp.id}>
                        {emp.name} ({emp.emp_code})
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item 
                name="loan_amount" 
                label="Loan Amount" 
                rules={[{ required: true, message: 'Please enter loan amount' }]}
              >
                <InputNumber 
                  style={{ width: '100%' }} 
                  min={0} 
                  placeholder="0.00"
                  formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/(,*)/g, '')}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item 
                name="monthly_deduction" 
                label="Monthly Deduction" 
                rules={[{ required: true, message: 'Please enter monthly deduction' }]}
              >
                <InputNumber 
                  style={{ width: '100%' }} 
                  min={0} 
                  placeholder="0.00"
                  formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/(,*)/g, '')}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item 
                name="loan_date" 
                label="Loan Date" 
                rules={[{ required: true, message: 'Please select loan date' }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="expected_completion_date" label="Expected Completion Date">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="purpose" label="Purpose">
                <Input placeholder="Loan purpose" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="remarks" label="Remarks">
            <TextArea rows={3} placeholder="Additional remarks" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Loan Modal */}
      <Modal
        title="Edit Loan"
        open={editModalVisible}
        onOk={() => editForm.submit()}
        onCancel={() => setEditModalVisible(false)}
        width={500}
      >
        <Form form={editForm} layout="vertical" onFinish={handleUpdateLoan}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="monthly_deduction" label="Monthly Deduction">
                <InputNumber 
                  style={{ width: '100%' }} 
                  min={0} 
                  placeholder="0.00"
                  formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/(,*)/g, '')}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="status" label="Status">
                <Select>
                  <Option value="active">Active</Option>
                  <Option value="completed">Completed</Option>
                  <Option value="cancelled">Cancelled</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="purpose" label="Purpose">
            <Input placeholder="Loan purpose" />
          </Form.Item>
          <Form.Item name="remarks" label="Remarks">
            <TextArea rows={3} placeholder="Additional remarks" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Loan Statement Modal */}
      <Modal
        title="Loan Statement"
        open={statementModalVisible}
        onCancel={() => setStatementModalVisible(false)}
        footer={null}
        width={800}
      >
        {loanStatement && (
          <div>
            <Card size="small" style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={8}>
                  <Statistic 
                    title="Loan Amount" 
                    value={loanStatement.loan.loan_amount} 
                    precision={2} 
                    formatter={val => formatNum(val)}
                  />
                </Col>
                <Col span={8}>
                  <Statistic 
                    title="Remaining Balance" 
                    value={loanStatement.loan.remaining_balance} 
                    precision={2} 
                    formatter={val => formatNum(val)}
                    valueStyle={{ color: '#cf1322' }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic 
                    title="Total Paid" 
                    value={loanStatement.loan.total_paid} 
                    precision={2} 
                    formatter={val => formatNum(val)}
                    valueStyle={{ color: '#3f8600' }}
                  />
                </Col>
              </Row>
              <Row gutter={16} style={{ marginTop: 16 }}>
                <Col span={12}>
                  <Text strong>Employee:</Text> {loanStatement.loan.employee.name}
                </Col>
                <Col span={12}>
                  <Text strong>Monthly Deduction:</Text> {formatNum(loanStatement.loan.monthly_deduction)}
                </Col>
              </Row>
            </Card>

            <Divider>Transaction History</Divider>

            <Table 
              dataSource={loanStatement.transactions} 
              columns={[
                {
                  title: 'Date',
                  dataIndex: 'created_at',
                  key: 'created_at',
                  render: (date) => moment(date).format('DD/MM/YYYY')
                },
                {
                  title: 'Type',
                  dataIndex: 'type',
                  key: 'type',
                  render: (type) => (
                    <Tag color={type === 'payment' ? 'green' : 'blue'}>
                      {type.toUpperCase()}
                    </Tag>
                  )
                },
                {
                  title: 'Amount',
                  dataIndex: 'amount',
                  key: 'amount',
                  render: (amount) => formatNum(amount)
                },
                {
                  title: 'Balance After',
                  dataIndex: 'balance_after',
                  key: 'balance_after',
                  render: (balance) => formatNum(balance)
                },
                {
                  title: 'Reference',
                  dataIndex: 'reference',
                  key: 'reference'
                },
                {
                  title: 'Remarks',
                  dataIndex: 'remarks',
                  key: 'remarks'
                }
              ]}
              rowKey="id"
              size="small"
              pagination={false}
            />
          </div>
        )}
      </Modal>

      {/* Manual Payment Modal */}
      <Modal
        title="Manual Payment"
        open={paymentModalVisible}
        onOk={() => paymentForm.submit()}
        onCancel={() => setPaymentModalVisible(false)}
        width={400}
      >
        {selectedLoan && (
          <div style={{ marginBottom: 16 }}>
            <Text>Employee: {selectedLoan.employee.name}</Text><br />
            <Text>Remaining Balance: {formatNum(selectedLoan.remaining_balance)}</Text>
          </div>
        )}
        <Form form={paymentForm} layout="vertical" onFinish={handleManualPayment}>
          <Form.Item 
            name="amount" 
            label="Payment Amount" 
            rules={[
              { required: true, message: 'Please enter payment amount' },
              {
                validator: (_, value) => {
                  if (value && value > selectedLoan?.remaining_balance) {
                    return Promise.reject('Amount cannot exceed remaining balance');
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <InputNumber 
              style={{ width: '100%' }} 
              min={0} 
              max={selectedLoan?.remaining_balance}
              placeholder="0.00"
              formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value.replace(/(,*)/g, '')}
            />
          </Form.Item>
          <Form.Item name="remarks" label="Remarks">
            <TextArea rows={3} placeholder="Payment remarks" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default EmployeeLoans; 